// BlogManager.jsx — Hexaplay Private Theater Booking
// Admin blog manager with SEO fields (incl. Image Alt Text).

import React, { useState, useEffect } from "react";
import "./BlogManager.css";
import axios from "axios";

/* ============================================================
   API CONFIGURATION
============================================================ */

const API = "https://www.thehexaplay.com/api/blogs";

const CATEGORIES = [
  "Private Theater",
  "Movie Nights",
  "Birthday Parties",
  "Couple Celebrations",
  "Booking Tips",
  "Product Updates",
];

/* SEO limits used for the live character counters */
const ALT_MAX = 125;
const META_TITLE_MAX = 60;
const META_DESC_MAX = 160;

export default function BlogManager() {
  const getInitialForm = () => ({
    id: "",
    slug: "",
    title: "",
    excerpt: "",
    content: "",
    featuredImage: "",
    imageAlt: "",
    category: "Private Theater",
    author: "Hexaplay Team",
    tags: [],
    metaTitle: "",
    metaDescription: "",
    pubDate: new Date().toISOString().slice(0, 10),
    status: "Published",
  });

  const [form, setForm] = useState(getInitialForm());
  const [tagInput, setTagInput] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ============================================================
     FETCH BLOGS
  ============================================================ */

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(API);

      const blogList = Array.isArray(response.data?.blogs)
        ? response.data.blogs
        : [];

      setBlogs(blogList);
    } catch (err) {
      console.error("Failed to fetch blogs:", err);

      setBlogs([]);

      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to fetch blogs"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     SLUG GENERATOR
  ============================================================ */

  const genSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  /* ============================================================
     TITLE CHANGE
  ============================================================ */

  const handleTitleChange = (e) => {
    const title = e.target.value;

    setForm((prev) => ({
      ...prev,
      title,
      slug: prev.slug || genSlug(title),
      metaTitle: prev.metaTitle || title,
    }));
  };

  /* ============================================================
     IMAGE UPLOAD
  ============================================================ */

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file.");
      return;
    }

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      setError("Image size must be less than 5MB.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        featuredImage: reader.result,
        /* Auto-suggest alt text from the title the first time */
        imageAlt: prev.imageAlt || prev.title || "",
      }));
    };

    reader.onerror = () => {
      setError("Failed to read image file.");
    };

    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setForm((prev) => ({
      ...prev,
      featuredImage: "",
    }));
  };

  /* ============================================================
     TAGS
  ============================================================ */

  const addTag = (e) => {
    e.preventDefault();

    const newTag = tagInput.trim();

    if (!newTag) return;

    if (form.tags.includes(newTag)) {
      setTagInput("");
      return;
    }

    setForm((prev) => ({
      ...prev,
      tags: [...prev.tags, newTag],
    }));

    setTagInput("");
  };

  const removeTag = (tagToRemove) => {
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  /* ============================================================
     SAVE BLOG
  ============================================================ */

  const saveBlog = async () => {
    if (!form.title.trim()) {
      alert("Please enter blog title.");
      return;
    }

    if (!form.slug.trim()) {
      alert("Please enter blog slug.");
      return;
    }

    if (!form.excerpt.trim()) {
      alert("Please enter blog excerpt.");
      return;
    }

    if (!form.content.trim()) {
      alert("Please enter blog content.");
      return;
    }

    /* SEO guard: an uploaded image must have alt text */
    if (form.featuredImage && !form.imageAlt.trim()) {
      alert(
        "Please add Image Alt Text for the featured image — it is important for SEO."
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const blogData = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        content: form.content,
        featuredImage: form.featuredImage || "",
        imageAlt: form.imageAlt.trim(),
        category: form.category,
        author: form.author,
        tags: Array.isArray(form.tags) ? form.tags : [],
        metaTitle: form.metaTitle || form.title,
        metaDescription: form.metaDescription || "",
        pubDate: form.pubDate,
        status: form.status,
      };

      if (editing && form.id) {
        await axios.put(`${API}/${form.id}`, blogData);
      } else {
        await axios.post(API, blogData);
      }

      await fetchBlogs();

      const wasEditing = editing;

      resetForm();

      alert(
        wasEditing
          ? "Blog updated successfully!"
          : "Blog published successfully!"
      );
    } catch (err) {
      console.error("Failed to save blog:", err);

      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to save blog"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     RESET FORM
  ============================================================ */

  const resetForm = () => {
    setForm(getInitialForm());
    setTagInput("");
    setEditing(false);
    setError("");
  };

  /* ============================================================
     EDIT BLOG
  ============================================================ */

  const editBlog = (blog) => {
    let formattedDate = new Date().toISOString().slice(0, 10);

    if (blog.pubDate) {
      const parsedDate = new Date(blog.pubDate);

      if (!Number.isNaN(parsedDate.getTime())) {
        formattedDate = parsedDate.toISOString().slice(0, 10);
      }
    }

    let blogTags = [];

    if (Array.isArray(blog.tags)) {
      blogTags = blog.tags;
    } else if (typeof blog.tags === "string") {
      blogTags = blog.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    }

    setForm({
      id: blog._id || blog.id || "",
      slug: blog.slug || "",
      title: blog.title || "",
      excerpt: blog.excerpt || "",
      content: blog.content || "",
      featuredImage: blog.featuredImage || "",
      imageAlt: blog.imageAlt || "",
      category: blog.category || "Private Theater",
      author: blog.author || "Hexaplay Team",
      tags: blogTags,
      metaTitle: blog.metaTitle || blog.title || "",
      metaDescription: blog.metaDescription || "",
      pubDate: formattedDate,
      status: blog.status || "Published",
    });

    setEditing(true);
    setError("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ============================================================
     DELETE BLOG
  ============================================================ */

  const deleteBlog = async (id) => {
    if (!id) {
      setError("Invalid blog ID.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this blog?"
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setError("");

      await axios.delete(`${API}/${id}`);

      await fetchBlogs();

      if (form.id === id) {
        resetForm();
      }

      alert("Blog deleted successfully!");
    } catch (err) {
      console.error("Failed to delete blog:", err);

      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to delete blog"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     FILTER BLOGS + STATS
  ============================================================ */

  const safeBlogs = Array.isArray(blogs) ? blogs : [];

  const filteredBlogs = safeBlogs.filter((blog) => {
    const title = blog?.title?.toLowerCase() || "";
    const slug = blog?.slug?.toLowerCase() || "";
    const search = searchTerm.toLowerCase();

    return title.includes(search) || slug.includes(search);
  });

  const publishedCount = safeBlogs.filter(
    (blog) => blog?.status === "Published"
  ).length;

  const draftCount = safeBlogs.filter(
    (blog) => blog?.status === "Draft"
  ).length;

  /* SEO health: published posts with complete SEO fields */
  const seoReadyCount = safeBlogs.filter(
    (blog) =>
      blog?.status === "Published" &&
      (blog.metaTitle || blog.title) &&
      (blog.metaDescription || blog.excerpt) &&
      (!blog.featuredImage || blog.imageAlt)
  ).length;

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <div className="blog-manager-container">
      <div className="blog-manager-main">
        {/* HEADER */}

        <div className="blog-manager-header">
          <div className="header-left">
            <div className="header-title-group">
              <div className="header-icon-badge">🎬</div>

              <div>
                <h1>Blog Manager</h1>

                <p>
                  Create &amp; manage SEO-optimized stories for
                  Hexaplay Private Theater
                </p>
              </div>
            </div>
          </div>

          <div className="blog-stats">
            <div className="stat-item total">
              <span className="stat-value">{safeBlogs.length}</span>
              <span>Total</span>
            </div>

            <div className="stat-item published">
              <span className="stat-value">{publishedCount}</span>
              <span>Published</span>
            </div>

            <div className="stat-item draft">
              <span className="stat-value">{draftCount}</span>
              <span>Draft</span>
            </div>

            <div className="stat-item seo">
              <span className="stat-value">{seoReadyCount}</span>
              <span>SEO Ready</span>
            </div>
          </div>
        </div>

        {/* ERROR */}

        {error && (
          <div className="error-message">
            <span>⚠️</span>

            <span>{error}</span>

            <button
              type="button"
              className="error-close"
              onClick={() => setError("")}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        )}

        {/* LOADING */}

        {loading && <div className="loading-overlay">Loading...</div>}

        {/* CONTENT */}

        <div className="blog-manager-content">
          {/* ============ FORM ============ */}

          <div className="blog-form-section">
            <div className="form-header">
              <h2>
                <span className="header-dot"></span>
                {editing
                  ? "Edit Blog Post"
                  : "Create New Blog Post"}
              </h2>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-publish"
                  onClick={saveBlog}
                  disabled={loading}
                >
                  {editing
                    ? "✓ Update Post"
                    : "🚀 Publish Post"}
                </button>

                <button
                  type="button"
                  className="btn-cancel"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>

            <div className="form-container">
              {/* ---------- SECTION 1 : ARTICLE DETAILS ---------- */}

              <section className="form-section-block">
                <div className="form-section-head">
                  <span className="form-section-num">01</span>

                  <div>
                    <h3>Article Details</h3>
                    <p>
                      Core content of your blog post
                    </p>
                  </div>
                </div>

                {/* TITLE */}

                <div className="form-row single">
                  <div className="form-group">
                    <label htmlFor="bm-title">
                      Blog Title <span className="req">*</span>
                    </label>

                    <input
                      id="bm-title"
                      type="text"
                      value={form.title}
                      onChange={handleTitleChange}
                      placeholder="e.g. 10 Ideas for an Unforgettable Private Theater Birthday Party"
                      className="form-control"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* SLUG + CATEGORY */}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="bm-slug">
                      Blog URL / Slug{" "}
                      <span className="req">*</span>
                    </label>

                    <input
                      id="bm-slug"
                      type="text"
                      value={form.slug}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          slug: genSlug(e.target.value),
                        }))
                      }
                      placeholder="private-theater-birthday-ideas"
                      className="form-control"
                      disabled={loading}
                    />

                    <small className="url-hint">
                      Final URL:{" "}
                      <b>
                        thehexaplay.com/blog/
                        {form.slug || "your-slug"}
                      </b>
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="bm-category">
                      Category{" "}
                      <span className="req">*</span>
                    </label>

                    <select
                      id="bm-category"
                      value={form.category}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      className="form-control"
                      disabled={loading}
                    >
                      {CATEGORIES.map((category) => (
                        <option
                          key={category}
                          value={category}
                        >
                          {category}
                        </option>
                      ))}
                    </select>

                    <small className="field-hint">
                      Drives the filter buttons on the public
                      blog page
                    </small>
                  </div>
                </div>

                {/* EXCERPT */}

                <div className="form-row single">
                  <div className="form-group">
                    <label htmlFor="bm-excerpt">
                      Excerpt <span className="req">*</span>
                    </label>

                    <textarea
                      id="bm-excerpt"
                      value={form.excerpt}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          excerpt: e.target.value,
                        }))
                      }
                      placeholder="Short summary shown on the blog cards — e.g. planning tips for a private theater celebration..."
                      className="form-control"
                      rows="4"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* CONTENT */}

                <div className="form-row single last">
                  <div className="form-group">
                    <label htmlFor="bm-content">
                      Blog Content{" "}
                      <span className="req">*</span>
                    </label>

                    <textarea
                      id="bm-content"
                      value={form.content}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          content: e.target.value,
                        }))
                      }
                      placeholder="Write the complete article here — you can use simple HTML like <h2>, <p>, <ul>, <img>..."
                      className="form-control content-editor"
                      rows="14"
                      disabled={loading}
                    />

                    <small className="field-hint">
                      Reading time is calculated automatically
                      from this content
                    </small>
                  </div>
                </div>
              </section>

              {/* ---------- SECTION 2 : MEDIA & AUTHOR ---------- */}

              <section className="form-section-block">
                <div className="form-section-head">
                  <span className="form-section-num">02</span>

                  <div>
                    <h3>Media &amp; Author</h3>
                    <p>
                      Featured image, alt text and byline
                    </p>
                  </div>
                </div>

                {/* FEATURED IMAGE + AUTHOR */}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="bm-image">
                      Featured Image
                    </label>

                    <input
                      id="bm-image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="form-control"
                      disabled={loading}
                    />

                    <small className="field-hint">
                      JPG / PNG / WebP — max 5MB, recommended
                      1200×630px
                    </small>

                    {form.featuredImage && (
                      <div className="image-preview-box">
                        <img
                          src={form.featuredImage}
                          alt={
                            form.imageAlt ||
                            "Featured image preview"
                          }
                        />

                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={removeImage}
                          aria-label="Remove featured image"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="bm-author">Author</label>

                    <input
                      id="bm-author"
                      type="text"
                      value={form.author}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          author: e.target.value,
                        }))
                      }
                      placeholder="Hexaplay Team"
                      className="form-control"
                      disabled={loading}
                    />

                    <small className="field-hint">
                      Shown on the blog card and article page
                    </small>
                  </div>
                </div>

                {/* IMAGE ALT TEXT (SEO) */}

                <div className="form-row single last">
                  <div className="form-group">
                    <label htmlFor="bm-imagealt">
                      Image Alt Text (SEO)
                      {form.featuredImage ? (
                        <span className="req"> *</span>
                      ) : null}
                    </label>

                    <input
                      id="bm-imagealt"
                      type="text"
                      value={form.imageAlt}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          imageAlt: e.target.value,
                        }))
                      }
                      placeholder="e.g. Couple enjoying a movie night inside a Hexaplay private theater"
                      className="form-control"
                      maxLength="200"
                      disabled={loading}
                    />

                    <div className="hint-row">
                      <small className="field-hint">
                        Describes the featured image for Google
                        &amp; screen readers — saved as the{" "}
                        <code>alt="..."</code> attribute on the
                        blog pages. Keep it descriptive, under
                        ~125 characters.
                      </small>

                      <small
                        className={`char-counter ${
                          form.imageAlt.length > ALT_MAX
                            ? "over"
                            : ""
                        }`}
                      >
                        {form.imageAlt.length}/{ALT_MAX}
                      </small>
                    </div>
                  </div>
                </div>
              </section>

              {/* ---------- SECTION 3 : TAGS ---------- */}

              <section className="form-section-block">
                <div className="form-section-head">
                  <span className="form-section-num">03</span>

                  <div>
                    <h3>Tags</h3>
                    <p>
                      Help readers find related articles
                    </p>
                  </div>
                </div>

                <div className="form-row single last">
                  <div className="form-group">
                    <label htmlFor="bm-taginput">Tags</label>

                    <div className="tags-wrap">
                      <input
                        id="bm-taginput"
                        type="text"
                        value={tagInput}
                        onChange={(e) =>
                          setTagInput(e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            addTag(e);
                          }
                        }}
                        placeholder="Enter tag and press Enter — e.g. birthday, couple, screening"
                        className="form-control"
                        disabled={loading}
                      />

                      <button
                        type="button"
                        className="add-tag-btn"
                        onClick={addTag}
                        disabled={loading}
                      >
                        + Add Tag
                      </button>
                    </div>

                    {form.tags.length > 0 && (
                      <div className="tags-list">
                        {form.tags.map((tag, index) => (
                          <span
                            key={`${tag}-${index}`}
                            className="tag-chip"
                          >
                            #{tag}

                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              aria-label={`Remove tag ${tag}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* ---------- SECTION 4 : SEO ---------- */}

              <section className="form-section-block">
                <div className="form-section-head">
                  <span className="form-section-num">04</span>

                  <div>
                    <h3>Search Engine Optimization</h3>
                    <p>
                      How this post appears in Google results
                    </p>
                  </div>
                </div>

                <div className="form-row last">
                  <div className="form-group">
                    <label htmlFor="bm-metatitle">
                      Meta Title
                    </label>

                    <input
                      id="bm-metatitle"
                      type="text"
                      value={form.metaTitle}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          metaTitle: e.target.value,
                        }))
                      }
                      placeholder="SEO meta title"
                      className="form-control"
                      maxLength="120"
                      disabled={loading}
                    />

                    <small
                      className={`char-counter ${
                        form.metaTitle.length > META_TITLE_MAX
                          ? "over"
                          : ""
                      }`}
                    >
                      {form.metaTitle.length}/
                      {META_TITLE_MAX} characters
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="bm-metadesc">
                      Meta Description
                    </label>

                    <textarea
                      id="bm-metadesc"
                      value={form.metaDescription}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          metaDescription: e.target.value,
                        }))
                      }
                      placeholder="SEO meta description"
                      className="form-control"
                      rows="3"
                      maxLength="200"
                      disabled={loading}
                    />

                    <small
                      className={`char-counter ${
                        form.metaDescription.length >
                        META_DESC_MAX
                          ? "over"
                          : ""
                      }`}
                    >
                      {form.metaDescription.length}/
                      {META_DESC_MAX} characters
                    </small>
                  </div>
                </div>
              </section>

              {/* ---------- SECTION 5 : PUBLISHING ---------- */}

              <section className="form-section-block">
                <div className="form-section-head">
                  <span className="form-section-num">05</span>

                  <div>
                    <h3>Publishing</h3>
                    <p>
                      Schedule and visibility of this post
                    </p>
                  </div>
                </div>

                <div className="form-row last">
                  <div className="form-group">
                    <label htmlFor="bm-pubdate">
                      Publication Date
                    </label>

                    <input
                      id="bm-pubdate"
                      type="date"
                      value={form.pubDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          pubDate: e.target.value,
                        }))
                      }
                      className="form-control"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="bm-status">Status</label>

                    <select
                      id="bm-status"
                      value={form.status}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="form-control"
                      disabled={loading}
                    >
                      <option value="Published">
                        Published — visible on the blog
                      </option>
                      <option value="Draft">
                        Draft — hidden from visitors
                      </option>
                    </select>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* ============ BLOG LIST ============ */}

          <div className="blog-list-section">
            <div className="list-header">
              <div className="list-header-left">
                <h2>
                  <span className="list-dot"></span>
                  All Blogs ({safeBlogs.length})
                </h2>
              </div>

              <div className="search-box">
                <span className="search-icon">🔍</span>

                <input
                  type="text"
                  placeholder="Search blogs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="blog-table-wrapper">
              {filteredBlogs.length > 0 ? (
                <table className="blog-table">
                  <thead>
                    <tr className="table-header">
                      <th>Image</th>
                      <th>Title / Slug</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredBlogs.map((blog) => (
                      <tr
                        key={blog._id || blog.id}
                        className="table-row"
                      >
                        <td>
                          <img
                            src={
                              blog.featuredImage ||
                              "https://via.placeholder.com/60x40"
                            }
                            alt={
                              blog.imageAlt ||
                              blog.title ||
                              "Blog"
                            }
                            className="blog-thumbnail"
                          />
                        </td>

                        <td>
                          <div className="blog-title">
                            {blog.title || "Untitled Blog"}
                          </div>

                          <div className="blog-slug-line">
                            <span className="blog-slug">
                              /blog/{blog.slug || ""}
                            </span>

                            <span
                              className={`alt-chip ${
                                blog.imageAlt
                                  ? "ok"
                                  : "missing"
                              }`}
                              title={
                                blog.imageAlt
                                  ? blog.imageAlt
                                  : "No image alt text — bad for SEO"
                              }
                            >
                              {blog.imageAlt
                                ? "Alt ✓"
                                : "Alt missing"}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div className="blog-date">
                            {blog.pubDate
                              ? new Date(
                                  blog.pubDate
                                ).toLocaleDateString()
                              : "-"}
                          </div>
                        </td>

                        <td>
                          <span
                            className={`status-badge ${(
                              blog.status || "Draft"
                            ).toLowerCase()}`}
                          >
                            {blog.status || "Draft"}
                          </span>
                        </td>

                        <td>
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="edit-btn"
                              onClick={() => editBlog(blog)}
                              disabled={loading}
                            >
                              ✏️ Edit
                            </button>

                            <button
                              type="button"
                              className="delete-btn"
                              onClick={() =>
                                deleteBlog(blog._id || blog.id)
                              }
                              disabled={loading}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-blogs">
                  <span className="no-blogs-icon">🎬</span>

                  <p>
                    {searchTerm
                      ? "No blogs match your search."
                      : "No blogs yet. Create your first Hexaplay story!"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}